import type {
    IConfigAPI,
    IFetcher,
    IFetchRequest,
    ILog,
    IProgressPromise,
    IProgressPromiseGenerator,
    IZip,
    Method,
} from '@monofe/util';
import { arrayBuffer } from '@monofe/util';
import type { ISiteData } from '../interface/siteData';

const DEFAULT_TIMEOUT_MS = 20000; /* 20s */
const DEFAULT_RETRY_COUNT = 3;

function _isBlob(object: unknown): object is Blob {
    try {
        return (
            object instanceof Blob ||
            ['[object Blob]', '[object File]'].includes(Object.prototype.toString.call(object))
        );
    } catch (error) {
        /* Blob typings missing (running on NodeJS) */
        return false;
    }
}

function _blobToString(blob: Blob): Promise<string> {
    return blob.text();
}

function _request(apiHref: string, authToken: string, hash: string): IFetchRequest {
    const isRunningOnNodeJS = typeof window === 'undefined';

    const req: IFetchRequest = {
        url: apiHref,
        method: 'POST' as Method,
        headers: [
            { name: 'Authorization', value: `Bearer ${authToken}` },
            { name: 'Content-Type', value: 'application/json' },
        ],
        body: JSON.stringify({ hash: hash }),
        expectedResponseType: isRunningOnNodeJS ? 'arraybuffer' : 'blob',
    };

    return req;
}

function _downloadSiteData(
    fetcher: IFetcher,
    promiser: IProgressPromiseGenerator,
    apiHref: string,
    authToken: string,
    hash: string,
    timeout: number,
    retryCount: number,
): IProgressPromise<ArrayBuffer | Blob> {
    return promiser.async(async progressCallback => {
        const req = _request(apiHref, authToken, hash);
        const promise = fetcher<ArrayBuffer | Blob>(req, timeout, retryCount);
        promise.onProgress(progressCallback);

        const response = await promise;
        const payload = response.payload;

        if (!payload) {
            throw new Error('Missing payload from response');
        }

        return payload;
    });
}

function _isISiteData(object: unknown): object is ISiteData {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }

    const record: Partial<ISiteData> = object as Partial<ISiteData>;

    return (
        !isNullOrUndefined(record.currentHash) &&
        !isNullOrUndefined(record.previousHash) &&
        !isNullOrUndefined(record.patch)
    );
}

const PATCH_DATA_FILE_NAME = 'data.json';

function _unzipSiteData(
    promiser: IProgressPromiseGenerator,
    zip: IZip,
    srcData: ArrayBuffer | Blob,
): IProgressPromise<ISiteData> {
    return promiser.async(async progressCallback => {
        const dataFile = await zip.unzipFile(srcData, PATCH_DATA_FILE_NAME);
        progressCallback(0.9);

        let dataStr = '';

        if (_isBlob(dataFile)) {
            dataStr = await _blobToString(dataFile);
        } else if (arrayBuffer.isArrayBuffer(dataFile)) {
            dataStr = arrayBuffer.arrayBufferToString(dataFile);
        } else {
            throw new Error(`unknown zip-file response type: ${JSON.stringify(dataFile)}`);
        }

        const jsonData = JSON.parse(dataStr) as unknown;

        if (!_isISiteData(jsonData)) {
            throw new Error('Response does not match expected ISiteData type');
        }

        return jsonData;
    });
}

function getSiteData(
    promiser: IProgressPromiseGenerator,
    fetcher: IFetcher,
    zipper: IZip,
    logger: ILog,
    config: IConfigAPI,
    authToken: string,
    hash = '',
    timeout = DEFAULT_TIMEOUT_MS,
    retryCount = DEFAULT_RETRY_COUNT,
): IProgressPromise<ISiteData> {
    return promiser.async(async progressCallback => {
        const apiHrefs = [`${config.url}${config.extension.siteData}`];

        let siteDataBinary: ArrayBuffer | Blob | null = null;

        for (const apiHref of apiHrefs) {
            const promise = _downloadSiteData(
                fetcher,
                promiser,
                apiHref,
                authToken,
                hash,
                timeout,
                retryCount,
            );

            promise.onProgress(data => {
                progressCallback((data * 0.9) / 2); /* 2 Jobs */
            });

            siteDataBinary = await promise.catch((error: Error) => {
                logger.error(
                    `Failed to download site data url:${apiHref}, hash:${hash}, token:${authToken}, error:${error.message}`,
                );
                return null;
            });

            if (siteDataBinary !== null) {
                break;
            }
        }

        if (siteDataBinary) {
            const unzipPromise = _unzipSiteData(promiser, zipper, siteDataBinary);
            unzipPromise.onProgress(data => {
                progressCallback(0.5 + data / 2); /* 2 Jobs */
            });
            const srcData = await unzipPromise;
            return srcData;
        } else {
            const message = `Unable to fetch site data from hrefs:${JSON.stringify(
                apiHrefs,
            )}, token:${authToken}, hash:${hash}`;
            throw new Error(message);
        }
    });
}

export default getSiteData;
